/**
 * Profile API — get/update user profile, upload avatar, change password.
 * Base URL: set VITE_API_BASE_URL in .env
 *
 * Endpoints:
 *   GET  /bot/api/auth/profile        → get current user's full profile
 *   PUT  /bot/api/auth/profile        → update profile (display_name, email, contact, language)
 *   POST /bot/api/auth/profile/avatar → upload avatar image (multipart/form-data)
 *   POST /bot/api/auth/change-password → change password (requires current password)
 */

import { getAuthHeaders } from './auth';
import { getBaseUrl, parseJson, buildUrl } from './utils';

/**
 * Get current user's full profile.
 * @returns {Promise<object>} { id, display_name, email, contact_number, role, avatar, company_id, language }
 */
export async function getProfile() {
  const url = buildUrl(getBaseUrl(), 'bot/api/auth/profile');
  const res = await fetch(url, {
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return parseJson(res);
}

/**
 * Update profile text fields.
 * @param {{ display_name: string, email?: string, contact_number?: string, language?: string }} data
 * @returns {Promise<object>} Updated user data
 */
export async function updateProfile(data) {
  const url = buildUrl(getBaseUrl(), 'bot/api/auth/profile');
  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(data),
  });
  return parseJson(res);
}

/**
 * Upload avatar image.
 * @param {File} file - Image file (jpg, png, webp; max 2MB)
 * @returns {Promise<object>} { avatar: string } — new avatar URL
 */
export async function uploadAvatar(file) {
  const url = buildUrl(getBaseUrl(), 'bot/api/auth/profile/avatar');
  const formData = new FormData();
  formData.append('avatar', file);
  const res = await fetch(url, {
    method: 'POST',
    headers: { ...getAuthHeaders() }, // Do NOT set Content-Type — let browser set multipart boundary
    body: formData,
  });
  return parseJson(res);
}

/**
 * Delete avatar image.
 * @returns {Promise<object>}
 */
export async function deleteAvatar() {
  const url = buildUrl(getBaseUrl(), 'bot/api/auth/profile/avatar');
  const res = await fetch(url, {
    method: 'DELETE',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return parseJson(res);
}

/**
 * Change password (requires current password verification).
 * @param {string} currentPassword
 * @param {string} newPassword
 * @returns {Promise<object>}
 */
export async function changePassword(currentPassword, newPassword) {
  const url = buildUrl(getBaseUrl(), 'bot/api/auth/change-password');
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  return parseJson(res);
}

/**
 * Email change — OTP to the CURRENT address (GS-2026-3402-4, Finding 4).
 * Works for every login type (Shopify / Google / password), since it proves
 * control of the current inbox rather than asking for a password.
 */

/**
 * Step 1: ask the server to email a 6-digit code to the current address.
 * @param {string} newEmail
 * @returns {Promise<object>} { status: "code_sent", message }
 */
export async function requestEmailChange(newEmail) {
  const url = buildUrl(getBaseUrl(), 'bot/api/auth/request-email-change');
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ new_email: newEmail }),
  });
  return parseJson(res);
}

/**
 * Step 2: submit the code to apply the change. Auth required (self-scoped).
 * @param {string} code
 * @returns {Promise<object>} { status: "success", email }
 */
export async function confirmEmailChange(code) {
  const url = buildUrl(getBaseUrl(), 'bot/api/auth/confirm-email-change');
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ code }),
  });
  return parseJson(res);
}

/**
 * Undo a completed change from the link mailed to the OLD address. The signed
 * token is the credential, so this does NOT require the user to be logged in.
 * @param {string} token
 * @returns {Promise<object>} { status, message, email }
 */
export async function revertEmailChange(token) {
  const url = buildUrl(getBaseUrl(), 'bot/api/auth/revert-email-change');
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ token }),
  });
  return parseJson(res);
}
